// Entry point. Sets the window.UserIntuitionRecorder marker FIRST, then
// decides -- purely from the URL fragment / sessionStorage -- whether to boot
// the recorder at all. With neither a fragment nor a resumed session, this
// module does nothing else: no network call, no rrweb.record(), no DOM
// changes beyond the marker object itself. That inertness is the whole point:
// this script sits on every page of the customer's site, and must only ever
// be active during an actual UserIntuition interview.

import { Channel } from './channel';
import { RECORDER_VERSION, SESSION_MAX_DURATION_MS } from './config';
import { mountConsentUI, type ConsentHandle } from './consent';
import { startCuratedTracking } from './curated';
import { parseSelectorAttr, type SelectorConfig } from './masking';
import { Outbox } from './outbox';
import { startRecording, type RecorderHandle } from './recorder';
import { clearSession, initSession, persistSession } from './session';
import type { AbortReason, RecorderMarker, Session } from './types';
import { canonicalChunkPayload, endpoints, postJson } from './wire';

const marker: RecorderMarker = {
  version: RECORDER_VERSION,
  active: false,
  stop: () => {},
};
window.UserIntuitionRecorder = marker;

/** Reads masking config off the currently-executing <script> tag. MUST be
 * called synchronously during initial script execution -- document.currentScript
 * is only valid for that window, even for `async` scripts. */
function readSelectorConfig(): SelectorConfig {
  const script = document.currentScript as HTMLScriptElement | null;
  return {
    mask: parseSelectorAttr(script?.getAttribute('data-ui-mask')),
    block: parseSelectorAttr(script?.getAttribute('data-ui-block')),
    ignore: parseSelectorAttr(script?.getAttribute('data-ui-ignore')),
  };
}

function boot(session: Session, selectors: SelectorConfig): void {
  marker.active = true;

  let stopped = false;
  let recorderHandle: RecorderHandle | null = null;
  let stopCurated: (() => void) | null = null;
  let consentHandle: ConsentHandle | null = null;
  let sessionTimer: ReturnType<typeof setTimeout> | null = null;
  // Guards finalizeAndStop against concurrent/repeated invocations -- see its
  // definition below.
  let finalizingPromise: Promise<void> | null = null;

  const outbox = new Outbox({
    recordingId: session.recordingId,
    token: session.token,
    onFinalizedOrAborted: () => stopAndClear('server_finalized_or_aborted'),
    onPersistentFailure: () => onIngestDegraded(),
    // pagehide/visibilitychange can happen between the recorder's 3-second
    // timer flushes. Convert that in-memory tail into a sequenced chunk before
    // the outbox offers its queue to sendBeacon; do not stop rrweb for a mere
    // backgrounding event because capture may continue when the page returns.
    onBeforeDrainSync: () => recorderHandle?.flush(),
  });

  const channel = new Channel({
    recordingId: session.recordingId,
    token: session.token,
    onFinalize: () => void finalizeAndStop(),
    onStopAndClear: () => stopAndClear('channel_closed'),
    // Lets the channel persist its monotonic /signal seq (signalSeq) into the
    // same __uirec blob as lastSeq, so it survives a same-origin navigation
    // instead of restarting and looking like replays of already-seen signals.
    session,
  });

  // Structured, single-line diagnostics so a session that ends unexpectedly
  // (a mid-interview teardown, an ingest outage) is explainable straight from
  // the recorded page's devtools -- the recorder has no other back-channel to
  // report on itself. Logging must never throw into the capture path.
  function logLifecycle(event: string, extra?: Record<string, unknown>): void {
    try {
      // eslint-disable-next-line no-console
      console.info('[UserIntuitionRecorder]', event, {
        recordingId: session.recordingId,
        uptimeMs: Date.now() - session.startedAt,
        lastSeq: recorderHandle?.getFinalSeq() ?? 0,
        pending: outbox.getPendingCount(),
        ...extra,
      });
    } catch {
      // diagnostics must never break capture
    }
  }

  // Chunk uploads have failed MAX_CONSECUTIVE_FLUSH_FAILURES times in a row.
  // Deliberately does NOT tear the session down: rrweb capture, the consent
  // UI, and the heartbeat/moderator feed are all independent of ingest health,
  // and the outbox keeps retrying (buffering to IndexedDB), so a transient
  // network/tunnel blip is survived and flushes on recovery -- instead of a
  // ~20s wobble nuking the whole session and forcing a screen-share fallback.
  // Trade-off: a genuinely sustained outage no longer self-aborts; the
  // recording just goes partial (bounded buffer) and is sealed at call end.
  function onIngestDegraded(): void {
    logLifecycle('ingest_degraded', {
      detail: 'chunk uploads failing; session stays live and keeps retrying',
    });
  }

  // clearData=true (the default) is the CLEAN-end path: everything captured
  // has been safely handed off (finalized) or is deliberately abandoned
  // (aborted/server-closed), so the durable outbox queue and the resumable
  // session are wiped. clearData=false is used ONLY when /finalize itself
  // failed -- teardown (timers/listeners/consent UI) still happens, but the
  // queued/captured data is left alone so it isn't destroyed just because the
  // sealing call didn't land.
  function stopAndClear(reason: string, opts: { clearData?: boolean } = {}): void {
    const clearData = opts.clearData ?? true;
    if (stopped) return;
    stopped = true;
    logLifecycle('session_ended', { reason });
    if (sessionTimer) clearTimeout(sessionTimer);
    recorderHandle?.stop();
    stopCurated?.();
    channel.stop();
    outbox.stop();
    if (clearData) {
      outbox.clearAll();
      clearSession();
    }
    consentHandle?.unmount();
    marker.active = false;
    marker.stop = () => {};
  }

  // NEVER call /finalize, /close, or /abort from a pagehide-driven path --
  // pagehide is handled entirely inside outbox.ts (drain via sendBeacon only).
  //
  // Guarded by `finalizingPromise` so repeated triggers (a duplicate
  // postMessage close, or /poll observing 'closing' more than once before
  // teardown completes) can never start a second, concurrent finalization --
  // every caller gets back the SAME in-flight promise.
  function finalizeAndStop(): Promise<void> {
    if (finalizingPromise) return finalizingPromise;
    finalizingPromise = (async () => {
      if (stopped) return;
      recorderHandle?.stop(); // 1. flush the final rrweb buffer into a chunk (idempotent -- stopAndClear also calls this)
      stopCurated?.(); // stop generating new curated events
      channel.quiesce(); // 2. stop producing signals/heartbeats/message-reactions (sendChain untouched)
      await outbox.flushUntilEmptyOrStuck(); // 3. drain queued chunks, now including the final one
      const finalSeq = recorderHandle?.getFinalSeq() ?? 0; // 4. true last seq, read AFTER the flush above
      await channel.drain(); // 5. await any /signal send still in flight
      const clientEndedAt = Date.now(); // 6. browser end time
      let ok = false;
      try {
        const res = await postJson(
          endpoints.finalize(session.recordingId),
          session.token,
          JSON.stringify({ final_seq: finalSeq, client_ended_at: clientEndedAt }),
        );
        ok = res.ok;
      } catch {
        // postJson itself never throws (it catches internally), but guard
        // defensively in case that contract ever changes.
        ok = false;
      }
      if (ok) {
        stopAndClear('finalized'); // 7a. clear IndexedDB/session -- the seal landed
      } else {
        stopAndClear('finalize_failed', { clearData: false }); // 7b. preserve captured-but-unacked data
      }
    })();
    return finalizingPromise;
  }

  async function abortAndStop(reason: AbortReason): Promise<void> {
    if (stopped) return;
    await postJson(endpoints.abort(session.recordingId), session.token, JSON.stringify({ reason }));
    stopAndClear(`aborted:${reason}`);
  }

  consentHandle = mountConsentUI({ onStop: () => void abortAndStop('participant_stopped') });
  marker.stop = () => void abortAndStop('participant_stopped');

  void outbox.init().then(() => {
    if (stopped) return;
    recorderHandle = startRecording({
      selectors,
      // Continue the seq from where a prior page left off (0 for a fresh
      // session). Without this, a resumed recorder restarts at seq 1 and its
      // chunks collide with the already-stored ones -> 409 -> false teardown.
      initialSeq: session.lastSeq,
      onChunk: (seq, events) => {
        // Persist the advancing seq BEFORE enqueue so the next page (after a
        // navigation) resumes the sequence instead of restarting it.
        session.lastSeq = seq;
        persistSession(session);
        const payload = canonicalChunkPayload(seq, events);
        outbox.enqueue(seq, payload);
      },
    });
    stopCurated = startCuratedTracking({
      selectors,
      onActivity: (activity) => channel.sendActivity(activity),
    });
    channel.start();
    sessionTimer = setTimeout(() => stopAndClear('session_max_duration'), SESSION_MAX_DURATION_MS);
  });
}

function init(): void {
  const session = initSession();
  if (!session) return; // inert: no fragment, no resumable session.
  boot(session, readSelectorConfig());
}

init();
